import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { EquipmentService } from 'src/app/equipment/equipment.service';
import { UserManagementService } from 'src/app/user-management/user-management.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-equipment-alerts-setup',
  templateUrl: './equipment-alerts-setup.component.html',
  styleUrls: ['./equipment-alerts-setup.component.css'],
  standalone: false,
  providers: [MessageService]
})
export class EquipmentAlertsSetupComponent implements OnInit {
  categoryId: string | number | null = null;
  fromCategory = false;
  selectedFile: File | null = null;
  isUploading = false;
  isProcessingFile = false;
  uploadSuccessful = false;
  isCreateAlertsMode = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private equipmentService: EquipmentService,
    private userService: UserManagementService,
    private messageService: MessageService,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    // Check if coming from category setting
    const params = this.activatedRoute.snapshot.queryParams;
    if (params['fromCategory'] === 'true') {
      this.fromCategory = true;
      this.categoryId = params['categoryId'] || null;
      console.log('=== EQUIPMENT & ALERTS SETUP ===');
      console.log('Category ID:', this.categoryId);
      console.log('From Category:', this.fromCategory);
    }
    
    // Check if in create alerts mode
    if (params['createAlerts'] === 'true') {
      this.isCreateAlertsMode = true;
      this.uploadSuccessful = true; // Assume upload was successful if we're in alerts mode
    }
    
    // Subscribe to query params changes
    this.activatedRoute.queryParams.subscribe(params => {
      if (params['fromCategory'] === 'true') {
        this.fromCategory = true;
        this.categoryId = params['categoryId'] || null;
      } else {
        this.fromCategory = false;
        this.categoryId = null;
      }
      
      if (params['createAlerts'] === 'true') {
        this.isCreateAlertsMode = true;
        this.uploadSuccessful = true;
      } else {
        this.isCreateAlertsMode = false;
      }
    });
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validExtensions = ['.xlsx', '.xls'];
      const fileName = file.name.toLowerCase();
      const isValidFile = validExtensions.some(ext => fileName.endsWith(ext));
      
      if (!isValidFile) {
        this.messageService.add({
          severity: 'error',
          summary: 'Invalid File',
          detail: 'Please select an Excel file (.xlsx or .xls)'
        });
        return;
      }
      
      // Show loading state while processing file selection
      this.isProcessingFile = true;
      
      // Simulate file processing delay (can be removed if not needed)
      setTimeout(() => {
        this.selectedFile = file;
        this.isProcessingFile = false;
        console.log('File selected:', file.name);
      }, 500);
    }
  }

  removeFile() {
    this.selectedFile = null;
    this.isProcessingFile = false;
    // Reset file input
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onUploadEquipment() {
    // Navigate to equipment setting page
    const queryParams: any = {};
    if (this.categoryId) {
      queryParams.categoryId = this.categoryId;
      queryParams.fromCategory = 'true';
    }
    
    this.router.navigate(['/equipment/equipmentsetting'], {
      queryParams: queryParams
    });
  }

  onSubmitFile() {
    if (!this.selectedFile) {
      return;
    }

    this.isUploading = true;
    
    // Read Excel file and process it
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const bstr: string = e.target.result;
        const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });
        const wsname: string = wb.SheetNames[0];
        const ws: XLSX.WorkSheet = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        
        // Process data to match the required payload format
        const equipementsData = this.processExcelData(data);
        
        // Get site ID
        const allSites = this.userService.getSiteDetail();
        const siteId = allSites?.[0]?.site_id;

        if (!siteId) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Site ID not found'
          });
          this.isUploading = false;
          return;
        }

        // Format data for API - Group by category and structure as per requirement
        // "Eq Category" should only appear on the first item in each category group
        const categoryMap = new Map<string, any[]>();
        let previousCategory = '';
        let categoryItemIndex = 0;

        equipementsData.forEach((row: any, index: number) => {
          // Get the category for this row (already processed by processExcelData)
          const eqCategory = row['Eq Category'];
          const currentCategory = eqCategory ? eqCategory.trim() : '';
          
          // If no category is set yet, skip this row
          if (!currentCategory) {
            console.warn(`Row ${index + 1} has no category assigned. Skipping...`);
            return;
          }

          // Detect category change - if category changed or this is the first row
          if (currentCategory !== previousCategory) {
            // New category detected
            previousCategory = currentCategory;
            categoryItemIndex = 0;
            if (!categoryMap.has(currentCategory)) {
              categoryMap.set(currentCategory, []);
            }
          }

          // Create equipment object with proper field names matching the payload format
          const equipmentItem: any = {
            'Equipment Type': row['Equipment Type'] || '',
            'Tag PID #': row['Tag PID #'] || '',
            'Description': row['Description'] || '',
            'Alert Text with Tag #': row['Alert Text with Tag #'] || ''
          };

          // Only add "Eq Category" to the first item in each category group
          if (categoryItemIndex === 0 && previousCategory) {
            equipmentItem['Eq Category'] = previousCategory;
          }

          // Add the equipment item to the active category's pump_type array
          const categoryArray = categoryMap.get(previousCategory) || [];
          categoryArray.push(equipmentItem);
          categoryMap.set(previousCategory, categoryArray);
          categoryItemIndex++;
        });

        // Build the all_category array structure
        const allCategory = Array.from(categoryMap.entries()).map(([categoryName, pumpTypeArray]) => {
          return {
            pump_type: pumpTypeArray
          };
        });

        // Build request payload matching the exact format
        const request: any = {
          purpose: 'addequipment',
          all_category: allCategory
        };
        
        // Add category_id if available
        if (this.categoryId) {
          request['category_id'] = this.categoryId.toString();
        }

        // Note: site_id is not included when fromCategory is true

        console.log('Equipment API Request Payload:', JSON.stringify(request, null, 2));

        // Only make API call when fromCategory is true
        if (!this.fromCategory) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Warning',
            detail: 'Equipment upload is only available when coming from category setup'
          });
          this.isUploading = false;
          return;
        }

        // Upload equipment data - use nodeUrl1 when fromCategory is true
        const equipmentURL = `${environment.nodeUrl1}equipment`;
        this.http.post(equipmentURL, request).toPromise().then(
          (response: any) => {
            this.isUploading = false;
            if (response?.status == 1) {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: response.msg || 'Equipment file uploaded successfully'
              });
              
              // Show success message in card
              this.uploadSuccessful = true;
              this.selectedFile = null;
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Upload Failed',
                detail: response.msg || 'Failed to upload equipment file'
              });
            }
          },
          (error) => {
            this.isUploading = false;
            console.error('Upload error:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Upload Error',
              detail: 'An error occurred while uploading the file'
            });
          }
        );
      } catch (error) {
        this.isUploading = false;
        console.error('File processing error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'File Processing Error',
          detail: 'Failed to process Excel file. Please check the file format.'
        });
      }
    };
    
    reader.readAsBinaryString(this.selectedFile);
  }

  processExcelData(data: any[]): any[] {
    // Skip header row and process data
    const headers = data[0];
    const rows = data.slice(1);
    
    // Normalize headers to handle variations in Excel column names
    const normalizedHeaders = headers.map((header: string) => {
      if (!header) return '';
      const headerLower = header.toString().toLowerCase().trim();
      // Map common variations to standard field names
      if (headerLower.includes('eq category') || headerLower.includes('category')) {
        return 'Eq Category';
      } else if (headerLower.includes('equipment type') || headerLower.includes('equipment_type')) {
        return 'Equipment Type';
      } else if (headerLower.includes('tag pid') || headerLower.includes('tag_pid')) {
        return 'Tag PID #';
      } else if (headerLower.includes('description')) {
        return 'Description';
      } else if (headerLower.includes('alert text') || headerLower.includes('alert_text')) {
        return 'Alert Text with Tag #';
      }
      return header.toString().trim();
    });
    
    let currentCategory = '';
    return rows.map((row: any, index: number) => {
      const rowData: any = {};
      normalizedHeaders.forEach((header: string, colIndex: number) => {
        if (header) {
          const value = row[colIndex];
          // Handle category detection - if "Eq Category" column has value, use it
          // Otherwise, if it's empty and we have a current category, keep using it
          if (header === 'Eq Category') {
            if (value) {
              currentCategory = value;
              rowData[header] = value;
            } else if (currentCategory) {
              rowData[header] = currentCategory;
            } else {
              rowData[header] = value || '';
            }
          } else {
            rowData[header] = value || '';
          }
        }
      });
      rowData.dataIndex = index;
      return rowData;
    });
  }

  clone(obj: any) {
    return JSON.parse(JSON.stringify(obj));
  }

  onCreateAlerts() {
    // Navigate to same page but in create alerts mode
    const queryParams: any = {
      createAlerts: 'true'
    };
    if (this.categoryId) {
      queryParams.categoryId = this.categoryId;
      queryParams.fromCategory = 'true';
    }
    
    this.router.navigate(['/equipment-alerts-setup'], {
      queryParams: queryParams
    });
  }

  onBack() {
    // Navigate back or to category list
    if (this.fromCategory) {
      this.router.navigate(['/category']);
    } else {
      this.router.navigate(['/reportlibrary/addreport']);
    }
  }
}

